/**
 * Planner API Client
 * Interfaces with orchestration-service planner endpoints
 */

// API base URL
const API_BASE = '/api/orchestration';

/**
 * Document for analysis
 */
export interface Document {
  id: string;
  title: string;
  content: string;
  type: string;
}

/**
 * Document analysis result
 */
export interface DocumentAnalysis {
  summary: string;
  keyFeatures: string[];
  technicalRequirements: string[];
  userStories: string[];
  risks: string[];
  dependencies: string[];
  estimatedComplexity: 'low' | 'medium' | 'high' | 'very_high';
}

/**
 * Generated story
 */
export interface GeneratedStory {
  id: string;
  title: string;
  description: string;
  acceptanceCriteria: string[];
  priority: 'critical' | 'high' | 'medium' | 'low';
  estimatedStoryPoints: number;
  technicalNotes?: string;
}

/**
 * Generated epic
 */
export interface GeneratedEpic {
  id: string;
  title: string;
  description: string;
  acceptanceCriteria: string[];
  priority: 'critical' | 'high' | 'medium' | 'low';
  estimatedStoryPoints: number;
  stories?: GeneratedStory[];
}

/**
 * Workplan
 */
export interface Workplan {
  id: string;
  projectId: string;
  title: string;
  description: string;
  analysis: DocumentAnalysis;
  status: 'draft' | 'approved' | 'in_progress' | 'completed' | 'archived';
  createdAt: string;
  epics: GeneratedEpic[];
  totalEpics: number;
  totalStories: number;
  totalPoints: number;
}

/**
 * Workplan list item
 */
export interface WorkplanListItem {
  id: string;
  projectId: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
}

/**
 * Generation job
 */
export interface GenerationJob {
  jobId: string;
  status: string;
  startTime: string;
  closeTime?: string;
  progress?: {
    step: string;
    progress: number;
    message: string;
  };
  result?: {
    workplanId: string;
    success: boolean;
    epics: GeneratedEpic[];
    duration: number;
  };
}

/**
 * Plan generation request
 */
export interface GeneratePlanRequest {
  projectId: string;
  projectTitle: string;
  documents: Document[];
  projectContext?: string;
  maxEpics?: number;
  maxStoriesPerEpic?: number;
  createIssues?: boolean;
  worktreeId?: string;
  boardId?: string;
}

/**
 * Planner API client
 */
export const plannerApi = {
  /**
   * Start plan generation
   */
  async generatePlan(request: GeneratePlanRequest): Promise<{ jobId: string; status: string }> {
    const response = await fetch(`${API_BASE}/planner/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(`Failed to start plan generation: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * List generation jobs
   */
  async listJobs(options?: { projectId?: string; limit?: number }): Promise<{ jobs: GenerationJob[]; count: number }> {
    const params = new URLSearchParams();
    if (options?.projectId) params.set('projectId', options.projectId);
    if (options?.limit) params.set('limit', String(options.limit));

    const response = await fetch(`${API_BASE}/planner/jobs?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to list jobs: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get job status
   */
  async getJob(jobId: string): Promise<GenerationJob> {
    const response = await fetch(`${API_BASE}/planner/jobs/${jobId}`);
    if (!response.ok) {
      throw new Error(`Failed to get job: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Cancel a job
   */
  async cancelJob(jobId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/planner/jobs/${jobId}/cancel`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to cancel job: ${response.statusText}`);
    }
  },

  /**
   * List workplans
   */
  async listWorkplans(options?: {
    projectId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ workplans: WorkplanListItem[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.projectId) params.set('projectId', options.projectId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));

    const response = await fetch(`${API_BASE}/workplans?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to list workplans: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Get workplan details
   */
  async getWorkplan(id: string): Promise<Workplan> {
    const response = await fetch(`${API_BASE}/workplans/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to get workplan: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Update workplan
   */
  async updateWorkplan(
    id: string,
    data: {
      status?: string;
      title?: string;
      description?: string;
      /**
       * Shift a fire time landing on Sat/Sun to the next business day.
       * Persisted as `skip_weekends` on the job_definition.
       */
      skipWeekends?: boolean;
      /**
       * When a fire lands on a holiday: 'previous' (previous business day),
       * 'next' (next business day), or 'skip' (run anyway). Persisted via the
       * holiday_calendar shift policy on the job_definition.
       */
      holidayShift?: 'previous' | 'next' | 'skip';
    }
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/workplans/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to update workplan: ${response.statusText}`);
    }
  },

  /**
   * Approve workplan
   */
  async approveWorkplan(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/workplans/${id}/approve`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to approve workplan: ${response.statusText}`);
    }
  },
};

export default plannerApi;
